import React, { useState } from 'react';
import { ShieldAlert, X, Plus, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { MODAL_OVERLAY_Z40_CLASS } from '../../ui/modalOverlay';
import { SEVERITES_RESTRICTION } from '../cantineUtils';

const TYPES_RESTRICTION = [
  { value: 'allergie', label: 'Allergie' },
  { value: 'regime', label: 'Régime alimentaire' }
];

const FORM_VIDE = { type: 'allergie', libelle: '', severite: 'important', observations: '' };

const ModalRestrictionsEleve = ({
  isOpen,
  onClose,
  eleve,
  restrictions,
  isLoading,
  isSaving,
  onAjouter,
  onSupprimer
}) => {
  const [form, setForm] = useState(FORM_VIDE);

  if (!isOpen || !eleve) return null;

  const handleAjouter = async () => {
    const ok = await onAjouter(form);
    if (ok) setForm(FORM_VIDE);
  };

  const severiteConfig = (severite) =>
    SEVERITES_RESTRICTION.find((s) => s.value === severite) || SEVERITES_RESTRICTION[2];

  return (
    <div className={`${MODAL_OVERLAY_Z40_CLASS} flex items-center justify-center p-4`}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="bg-[#c66d59] p-6 flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-white/80">Sécurité alimentaire</p>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <ShieldAlert size={24} />
              Allergies et régimes
            </h3>
            <p className="text-sm text-white/90 mt-1">
              {eleve.prenom} {eleve.nom} · {eleve.classe_nom || '—'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">
                Une restriction marquée <strong>critique</strong> remonte en alerte sur la feuille de pointage
                du jour, pour être visible de l'équipe au moment du service.
              </p>
            </div>
          </div>

          {/* Liste existante */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              Restrictions enregistrées ({restrictions.length})
            </h4>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={22} className="animate-spin text-[#246990]" />
              </div>
            ) : restrictions.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <ShieldAlert size={36} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 font-medium">Aucune restriction connue</p>
                <p className="text-xs text-gray-500 mt-1">Cet élève peut recevoir le menu standard.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {restrictions.map((restriction) => {
                  const config = severiteConfig(restriction.severite);
                  return (
                    <div
                      key={restriction.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{restriction.libelle}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                            {config.label}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {restriction.type === 'allergie' ? 'Allergie' : 'Régime'}
                          </span>
                        </div>
                        {restriction.observations && (
                          <p className="text-xs text-gray-500 mt-1">{restriction.observations}</p>
                        )}
                      </div>
                      <button
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors flex-shrink-0"
                        title="Supprimer"
                        onClick={() => onSupprimer(restriction)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Ajout */}
          <div className="border-2 border-gray-200 rounded-lg bg-gray-50/50">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <h4 className="text-base font-semibold text-gray-900">Ajouter une restriction</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES_RESTRICTION.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sévérité</label>
                  <Select value={form.severite} onValueChange={(v) => setForm({ ...form, severite: v })}>
                    <SelectTrigger className="bg-white border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITES_RESTRICTION.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Libellé <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="Ex : arachide, sans porc, végétarien..."
                  value={form.libelle}
                  onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                  className="bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Précision (optionnelle)</label>
                <textarea
                  value={form.observations}
                  onChange={(e) => setForm({ ...form, observations: e.target.value })}
                  placeholder="Conduite à tenir, contact en cas de réaction..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                />
              </div>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  className="bg-[#c66d59] hover:bg-[#ae7467] text-white"
                  onClick={handleAjouter}
                  disabled={isSaving || !form.libelle.trim()}
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Ajout...
                    </>
                  ) : (
                    <>
                      <Plus size={14} className="mr-2" />
                      Ajouter
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" size="default" onClick={onClose}>Fermer</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalRestrictionsEleve;
